import React from 'react';
// import { Info } from 'lucide-react';
import { useObservationValue } from '../../hooks/useObservationValue';
import { ObservableLevelEnumValue } from '../../data/enums';
import { Checkbox } from './BaseCheckbox';
import { Tooltip } from './Tooltip';
import { Label } from './Label';

const serializeBoolean = (v: boolean) => String(v);
const deserializeBoolean = (raw: string) => raw === 'true';

interface CheckboxWithComponentsProps {
  title?: string;
  label: string;
  infoTooltipKey?: string;
  onChange?: (checked: boolean) => void;
  onDefaultLoad?: (checked: boolean) => void;
  path: string;
  dataLevel: ObservableLevelEnumValue;
  entityId: number;
  entityExternalId: string;
  inspectionId: string;
  inspectionCategoryTypeId: string;
  childPaths: string[];
  children?: React.ReactNode;
}

export const CheckboxWithComponents: React.FC<CheckboxWithComponentsProps> = ({
  title,
  label,
  infoTooltipKey,
  path,
  dataLevel,
  entityId,
  entityExternalId,
  inspectionId,
  inspectionCategoryTypeId,
  onChange,
  onDefaultLoad,
  childPaths,
  children,
}) => {
  const field = useObservationValue<boolean>({
    inspectionId,
    inspectionCategoryId: inspectionCategoryTypeId,
    dataLevel,
    entityId,
    entityExternalId,
    path,
    onDefaultLoad,
    serialize: serializeBoolean,
    deserialize: deserializeBoolean,
  });
  const isChecked = field.value;

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const checked = event.target.checked;
    field.setValue(checked);

    // success = true always in draft mode (already durable); in legacy mode it mirrors the
    // real upsertObservation outcome, so children only clear once the checkbox's own value
    // is confirmed persisted - matching the original legacy contract exactly.
    const success = await field.commit(checked);
    if (success) {
      if (!checked) {
        for (const childPath of childPaths) {
          void field.removeRelated(childPath);
        }
      }
      onChange?.(checked);
    }
  };

  return (
    <div className="w-full space-y-2 py-2">
      <div className="flex items-start justify-between">
        {title && (
          <div className="w-full pr-8 pb-4 lg:w-1/2">
            <Label titleType="fieldTitle">{title}</Label>
          </div>
        )}
        {title && infoTooltipKey && (
          <Tooltip tooltipKey={infoTooltipKey} useMouseHover />
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex w-full cursor-pointer items-center space-x-2 py-2 hover:bg-brandLight/30">
          <Checkbox
            name={`checkbox-${path}`}
            checked={isChecked}
            onChange={handleChange}
            className="checked:bg-brandOrange"
          />
          <label
            htmlFor={`checkbox-${path}`}
            className="flex-1 cursor-pointer text-sm text-ink hover:bg-brandLight/30"
          >
            {label}
          </label>
        </div>
        {!title && infoTooltipKey && (
          <Tooltip tooltipKey={infoTooltipKey} useMouseHover />
        )}
      </div>

      <div>{isChecked && <div className="mt-4">{children}</div>}</div>
    </div>
  );
};
